/**
 * Managed Axios Instance
 * Automatically cancels all pending requests on route change
 * Includes auth token interceptor
 */
import axios from 'axios';

// Store all pending request controllers
const pendingRequests = new Map();
let requestId = 0;

// Create axios instance
const managedAxios = axios.create();

// Request interceptor - add auth token and abort controller to every request
managedAxios.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const authData = localStorage.getItem("ranker-auth");
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const token = parsed.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        console.error("Error parsing auth data:", e);
      }
    }
    
    // Fallback: Check direct token storage (some pages use this)
    if (!config.headers?.Authorization) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Only create abort controller if no signal was provided
    // This allows components to pass their own signal for local cancellation
    if (!config.signal) {
      const controller = new AbortController();
      config.signal = controller.signal;
      
      // Generate unique ID and store controller
      const id = ++requestId;
      config._requestId = id;
      pendingRequests.set(id, controller);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - cleanup completed requests
managedAxios.interceptors.response.use(
  (response) => {
    // Remove from pending on success (only if we added it)
    if (response.config._requestId) {
      pendingRequests.delete(response.config._requestId);
    }
    return response;
  },
  (error) => {
    // Remove from pending on error (only if we added it)
    if (error.config?._requestId) {
      pendingRequests.delete(error.config._requestId);
    }
    return Promise.reject(error);
  }
);

/**
 * Cancel all pending requests
 * Called on route change
 */
export const cancelAllRequests = () => {
  pendingRequests.forEach((controller) => {
    try {
      controller.abort();
    } catch (e) {
      // Silently ignore abort errors
    }
  });
  pendingRequests.clear();
};

/**
 * Get count of pending requests
 */
export const getPendingCount = () => pendingRequests.size;

/**
 * Check if an error is a cancellation error
 */
export const isCancelError = (error) => {
  return error?.name === 'CanceledError' || 
         error?.code === 'ERR_CANCELED' ||
         error?.name === 'AbortError' ||
         axios.isCancel(error);
};

export default managedAxios;
